import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Megaphone } from 'lucide-react';
import { useBannerClick, useBannerImpression } from './adPreview';
import type { PromoControls } from './adBanners';

/**
 * Haus-Anzeige „Hier werben" im Landing-Feed (Abschnitt 15, Punkt 2 des Business-Audits): der
 * Werbeplatz bewirbt sich selbst, solange ihn niemand gebucht hat. Teilt sich den Slot unter
 * „n EVENTS" mit dem Veranstalter-Banner, gewechselt von `PromoBannerSlot`.
 *
 * Gebaut wie der Veranstalter-Banner (Karte statt Bild, Maße aus `docs/werbung-spezifikation.md`,
 * Abschnitt 2), nur im Farbverlauf der Seite „Für Partner" — der Klick landet dort, und beides
 * gehört sichtbar zusammen.
 *
 * Der Text nennt die drei Disziplinen statt einer Reichweitenzahl: Zahlen gehören ins Gespräch
 * und auf die Seite „Für Partner", wo sie belegt und gepflegt werden. Ein Wert im Banner wäre
 * schon nach einer Saison falsch, ohne dass es jemandem auffällt
 * (`docs/traffic-baseline-2026-07.md`).
 *
 * Gezählt wird wie bei jedem Werbeplatz als `AD_BANNER`-Slot `partner-promo` (Einblendung und
 * Klick unter /admin/tracking). Die Anfrage selbst steht danach im Posteingang unter
 * „Werbeplatz" — siehe `FuerPartner.tsx`.
 */
const SLOT = 'partner-promo';

const eyebrow: React.CSSProperties = { fontFamily: "'Space Mono',monospace", letterSpacing: '0.14em', color: '#D08BA3' };
const headline: React.CSSProperties = { fontFamily: "'Archivo',sans-serif", fontWeight: 800, color: '#fff', lineHeight: 1.1 };
const subline: React.CSSProperties = { fontFamily: "'Hanken Grotesk',sans-serif", color: '#D8CDD3' };

const EYEBROW = 'WERBEN AUF RADSPORT-EVENTS';
const HEADLINE = 'Deine Marke. Mitten im Radsport.';
const SUBLINE = 'Erreiche eine aktive Community aus Rennrad-, Gravel- und MTB-Fahrern.';

function Cta({ placement, style }: { placement: string; style?: React.CSSProperties }) {
  const trackClick = useBannerClick(SLOT);
  return (
    <Link
      to="/fuer-partner?via=banner"
      onClick={() => trackClick(placement)}
      className="organizer-promo-btn"
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#fff', color: '#922C57', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 14, padding: '12px 20px', borderRadius: 999, whiteSpace: 'nowrap', textDecoration: 'none', ...style }}
    >
      Werbepartner werden <ArrowRight size={16} aria-hidden />
    </Link>
  );
}

/**
 * `controls` zeichnet der Slot: Pfeile am linken und rechten Rand, die Punkte mittig unten. Auf
 * dem Handy stehen beide als eigene Zeile unter dem Knopf. Steht die Anzeige allein (Design-Lab),
 * bleibt der Platz leer und die Karte sieht aus wie eine gebuchte Platzierung.
 */
export default function PartnerPromoBanner({ controls }: { controls?: PromoControls }) {
  useBannerImpression(SLOT);

  // Ohne Bedienelemente bleibt die Karte, wie sie in der Spezifikation steht; mit ihnen braucht
  // sie links und rechts Platz für die Pfeile und unten für die Punkte.
  const desktopPadding = controls ? '14px 46px 24px 46px' : '16px 22px 16px 20px';

  // Beide Anzeigen sind auf dem Handy gleich hoch, sonst rückt die Eventliste bei jedem Wechsel
  // ein Stück nach oben (Gegenstück in `DeltaBikePromoBanner.css`). Kompakt, weil die Anzeige auf
  // dem Handy zwischen den Event-Cards steht. Die Punkte liegen im unteren Rand, daher dort mehr Abstand.
  const mobileCard: React.CSSProperties = controls ? { minHeight: 172, paddingBottom: 24 } : {};

  return (
    <>
      {/* Desktop-Leaderboard */}
      <div
        className="hide-mobile"
        style={{ position: 'relative', overflow: 'hidden', borderRadius: 12, background: 'linear-gradient(100deg,#3A1523 0%,#6E2440 58%,#A8355F 100%)', padding: desktopPadding, display: 'flex', alignItems: 'center', gap: 18 }}
      >
        <div style={{ position: 'absolute', right: -30, top: -30, width: 130, height: 130, background: '#C25078', opacity: 0.14, transform: 'rotate(45deg)', borderRadius: 26, pointerEvents: 'none' }} />
        <span className="promo-banner-icon" style={{ flexShrink: 0, width: 52, height: 52, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 14, background: 'rgba(255,255,255,0.12)' }}>
          <Megaphone size={26} strokeWidth={1.8} color="#fff" aria-hidden />
        </span>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <div style={{ ...eyebrow, fontSize: 10 }}>{EYEBROW}</div>
          {/* Skaliert mit der Breite: in der 614px-Spalte eines 1024er-Bildschirms bräche die
              Headline bei 21px neben dem Knopf um. */}
          <div style={{ ...headline, fontSize: 'clamp(17px, 1.65vw, 21px)', marginTop: 5 }}>{HEADLINE}</div>
          <div style={{ ...subline, fontSize: 13, marginTop: 3 }}>{SUBLINE}</div>
        </div>
        <Cta placement="landing-desktop" style={{ position: 'relative', flexShrink: 0 }} />
        {controls?.('desktop')}
      </div>

      {/* Handy-Karte */}
      <div
        className="hide-desktop promo-card-mobile"
        style={{ position: 'relative', overflow: 'hidden', borderRadius: 14, background: 'linear-gradient(150deg,#3A1523 0%,#6E2440 66%,#A8355F 120%)', padding: '14px 16px', ...mobileCard }}
      >
        <div style={{ position: 'absolute', right: -34, top: -34, width: 130, height: 130, background: '#C25078', opacity: 0.16, transform: 'rotate(45deg)', borderRadius: 26, pointerEvents: 'none' }} />
        <div style={{ ...eyebrow, fontSize: 10, letterSpacing: '0.12em', position: 'relative' }}>{EYEBROW}</div>
        {/* Skaliert mit der Breite, damit die Headline auf einer Zeile bleibt: bei 20px bräche sie
            auf 360px-Geräten in zwei Zeilen. Ab etwa 430px steht wieder die volle Größe. */}
        <div style={{ ...headline, fontSize: 'clamp(15.5px, 4.8vw, 18px)', margin: '6px 0 3px', position: 'relative' }}>{HEADLINE}</div>
        <div className="promo-card-subline" style={{ ...subline, fontSize: 13, lineHeight: 1.35, position: 'relative' }}>{SUBLINE}</div>
        <div className="promo-card-action" style={{ position: 'relative', marginTop: 10 }}>
          <Cta placement="landing-mobile" style={{ width: '100%', padding: '9px 20px' }} />
        </div>
        {controls?.('mobile')}
      </div>
    </>
  );
}
